import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import {
  dismissRecommendation,
  fetchDismissedRecommendations,
  fetchRecommendations,
  refreshRecommendations,
  setDismissReason,
  topupRecommendations,
  undismissRecommendation,
} from '@/api/recommendations'
import { addToWishlist, promoteToPlaying, promoteToPlayed } from '@/api/wishlist'
import { fetchGroupSummary, fetchGroups, updateGroup } from '@/api/groups'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import { GroupHeader } from '@/components/groups/GroupHeader'
import { GameDetailModal } from '@/components/wishlist/GameDetailModal'
import { Icon } from '@/components/ui/Icon'
import { DismissedRecommendationsModal } from '@/components/recommendations/DismissedRecommendationsModal'
import { RecTuning } from '@/components/recommendations/RecTuning'
import { UndoDismissToast } from '@/components/recommendations/UndoDismissToast'
import { toFriend } from '@/lib/steam'
import type {
  DismissReason,
  DismissedRecommendationEntry,
  DismissedRecommendationsResponse,
  Group,
  RecommendationsResponse,
} from '@/types'
import {
  RecommendationsView,
  type RecommendationPending,
  type WishlistRecommendationViewEntry,
} from './RecommendationsView'

// How long to wait before giving up on the WebSocket notification and doing one
// manual refetch. Covers local dev (no WebSocket) and WebSocket connection gaps.
// Sized to outlast the backend's LLM retry budget (~16s of backoff + 3 attempts of
// ~10s generation = ~45s worst case) plus headroom.
const FALLBACK_REFETCH_MS = 120_000

const WISHLIST_TOPUP_THRESHOLD = 5
const DISCOVERY_TOPUP_THRESHOLD = 10

const TOAST_DURATION_MS = 6000
const TOAST_CONFIRM_DURATION_MS = 1800

interface ToastState {
  appId: string
  title: string
  confirmedReason: DismissReason | null
}

export default function RecommendationsPage() {
  const { activeGroupId } = useUIStore()
  const currentUser = useAuthStore((s) => s.user)
  const { canEditContent } = useGroupPermissions()
  const queryClient = useQueryClient()

  const [isGenerating, setIsGenerating] = useState(false)
  const [detailGameId, setDetailGameId] = useState<string | null>(null)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [showDismissedModal, setShowDismissedModal] = useState(false)
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false)
  const prevGeneratedAtRef = useRef<string | null | undefined>(undefined)
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const topupFiredRef = useRef(false)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearToastTimer = () => {
    if (toastTimerRef.current !== null) {
      clearTimeout(toastTimerRef.current)
      toastTimerRef.current = null
    }
  }

  // Reset toast/modal when the active group changes so context doesn't leak across groups.
  useEffect(() => {
    setToast(null)
    setShowDismissedModal(false)
    setShowRefreshPrompt(false)
    clearToastTimer()
  }, [activeGroupId])

  useEffect(() => clearToastTimer, [])

  const { data, isLoading } = useQuery({
    queryKey: ['recommendations', activeGroupId],
    queryFn: () => fetchRecommendations(activeGroupId!),
    enabled: !!activeGroupId,
  })

  const { data: dismissedData } = useQuery({
    queryKey: ['recommendations-dismissed', activeGroupId],
    queryFn: () => fetchDismissedRecommendations(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const dismissedEntries: DismissedRecommendationEntry[] = dismissedData?.entries ?? []

  useEffect(() => {
    if (!isGenerating) return
    const newGeneratedAt = data?.generatedAt ?? null
    if (newGeneratedAt !== prevGeneratedAtRef.current && newGeneratedAt !== null) {
      setIsGenerating(false)
      if (fallbackTimerRef.current !== null) {
        clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = null
      }
    }
  }, [data?.generatedAt, isGenerating])

  const { data: groups = [] } = useQuery({ queryKey: ['groups'], queryFn: fetchGroups })
  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const members = (summary?.members ?? []).map(toFriend)
  const friendById = new Map(members.map((m) => [m.id, m]))

  const refreshMutation = useMutation({
    mutationFn: () => refreshRecommendations(activeGroupId!),
    onSuccess: () => {
      prevGeneratedAtRef.current = data?.generatedAt ?? null
      setIsGenerating(true)
      fallbackTimerRef.current = setTimeout(() => {
        fallbackTimerRef.current = null
        void queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
        setIsGenerating(false)
      }, FALLBACK_REFETCH_MS)
    },
  })

  const saveGuidanceMutation = useMutation({
    mutationFn: (guidance: string) => updateGroup(activeGroupId!, { guidance }),
    onMutate: (guidance) => {
      const previous = queryClient.getQueryData<Group[]>(['groups'])
      queryClient.setQueryData<Group[]>(['groups'], (old) =>
        (old ?? []).map((g) => (g.id === activeGroupId ? { ...g, guidance } : g)),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['groups'], context.previous)
      // Roll back the prompt too — there's nothing to refresh if the save failed.
      setShowRefreshPrompt(false)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
  })

  const handleSaveGuidance = (text: string) => {
    const prev = (activeGroup?.guidance ?? '').trim()
    if (text.trim() !== prev) {
      // Show the refresh nudge synchronously with the click — the optimistic update
      // means the user sees their saved prefs immediately, and the prompt offers a
      // one-click way to materialize them in recommendations now instead of waiting
      // for the daily cron.
      setShowRefreshPrompt(true)
    }
    saveGuidanceMutation.mutate(text)
  }

  const topupMutation = useMutation({
    mutationFn: () => topupRecommendations(activeGroupId!),
    onSuccess: () => {
      prevGeneratedAtRef.current = data?.generatedAt ?? null
      setIsGenerating(true)
      fallbackTimerRef.current = setTimeout(() => {
        fallbackTimerRef.current = null
        void queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
        setIsGenerating(false)
      }, FALLBACK_REFETCH_MS)
    },
  })

  function patchOut(appId: string): {
    previous: RecommendationsResponse | undefined
  } {
    queryClient.cancelQueries({ queryKey: ['recommendations', activeGroupId] })
    const previous = queryClient.getQueryData<RecommendationsResponse>([
      'recommendations',
      activeGroupId,
    ])
    queryClient.setQueryData<RecommendationsResponse>(
      ['recommendations', activeGroupId],
      (old) => {
        if (!old) return old
        return {
          ...old,
          entries: old.entries.filter((e) => e.appId !== appId),
          wishlistEntries: old.wishlistEntries.filter((e) => e.appId !== appId),
        }
      },
    )
    return { previous }
  }

  function findEntryGame(appId: string) {
    const live = queryClient.getQueryData<RecommendationsResponse>([
      'recommendations',
      activeGroupId,
    ])
    return (
      live?.entries.find((e) => e.appId === appId)?.game ??
      live?.wishlistEntries.find((e) => e.appId === appId)?.game ??
      null
    )
  }

  const dismissMutation = useMutation({
    mutationFn: ({ appId, reason }: { appId: string; reason: DismissReason | null }) =>
      dismissRecommendation(activeGroupId!, appId, reason),
    onMutate: ({ appId, reason }) => {
      const { previous } = patchOut(appId)
      const game = findEntryGame(appId)
      const previousDismissed = queryClient.getQueryData<DismissedRecommendationsResponse>([
        'recommendations-dismissed',
        activeGroupId,
      ])
      if (game) {
        const stub: DismissedRecommendationEntry = {
          appId,
          reason,
          dismissedAt: new Date().toISOString(),
          dismissedBy: currentUser?.steamId ?? '',
          game,
        }
        queryClient.setQueryData<DismissedRecommendationsResponse>(
          ['recommendations-dismissed', activeGroupId],
          (old) => ({
            entries: [stub, ...(old?.entries.filter((e) => e.appId !== appId) ?? [])],
          }),
        )
      }
      return { previous, previousDismissed }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['recommendations', activeGroupId], context.previous)
      if (context?.previousDismissed)
        queryClient.setQueryData(
          ['recommendations-dismissed', activeGroupId],
          context.previousDismissed,
        )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['recommendations-dismissed', activeGroupId] })
    },
  })

  const restoreMutation = useMutation({
    mutationFn: (appId: string) => undismissRecommendation(activeGroupId!, appId),
    onMutate: (appId) => {
      const previousDismissed = queryClient.getQueryData<DismissedRecommendationsResponse>([
        'recommendations-dismissed',
        activeGroupId,
      ])
      queryClient.setQueryData<DismissedRecommendationsResponse>(
        ['recommendations-dismissed', activeGroupId],
        (old) => ({ entries: old?.entries.filter((e) => e.appId !== appId) ?? [] }),
      )
      return { previousDismissed }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousDismissed)
        queryClient.setQueryData(
          ['recommendations-dismissed', activeGroupId],
          context.previousDismissed,
        )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['recommendations-dismissed', activeGroupId] })
    },
  })

  const setReasonMutation = useMutation({
    mutationFn: ({ appId, reason }: { appId: string; reason: DismissReason | null }) =>
      setDismissReason(activeGroupId!, appId, reason),
    onMutate: ({ appId, reason }) => {
      const previousDismissed = queryClient.getQueryData<DismissedRecommendationsResponse>([
        'recommendations-dismissed',
        activeGroupId,
      ])
      queryClient.setQueryData<DismissedRecommendationsResponse>(
        ['recommendations-dismissed', activeGroupId],
        (old) => ({
          entries: (old?.entries ?? []).map((e) => (e.appId === appId ? { ...e, reason } : e)),
        }),
      )
      return { previousDismissed }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousDismissed)
        queryClient.setQueryData(
          ['recommendations-dismissed', activeGroupId],
          context.previousDismissed,
        )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations-dismissed', activeGroupId] })
    },
  })

  const addToWishlistMutation = useMutation({
    mutationFn: (appId: string) => addToWishlist(activeGroupId!, appId),
    onMutate: (appId) => patchOut(appId),
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['recommendations', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
    },
  })

  const addToPlayingMutation = useMutation({
    mutationFn: (appId: string) => promoteToPlaying(activeGroupId!, appId),
    onMutate: (appId) => patchOut(appId),
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['recommendations', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
    },
  })

  const addToPlayedMutation = useMutation({
    mutationFn: (appId: string) => promoteToPlayed(activeGroupId!, appId),
    onMutate: (appId) => patchOut(appId),
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['recommendations', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
    },
  })

  const entries = data?.entries ?? []
  const wishlistEntriesRaw = data?.wishlistEntries ?? []

  const wishlistEntries: WishlistRecommendationViewEntry[] = wishlistEntriesRaw.map((e) => ({
    ...e,
    ownedBy: [],
    wishers: e.wishlistedBy
      .map((id) => friendById.get(id))
      .filter((f): f is NonNullable<typeof f> => f !== undefined),
  }))

  const totalEntries = entries.length + wishlistEntries.length
  const needsTopup =
    entries.length <= DISCOVERY_TOPUP_THRESHOLD ||
    wishlistEntries.length <= WISHLIST_TOPUP_THRESHOLD

  useEffect(() => {
    if (
      entries.length > DISCOVERY_TOPUP_THRESHOLD &&
      wishlistEntries.length > WISHLIST_TOPUP_THRESHOLD
    ) {
      topupFiredRef.current = false
    } else if (totalEntries === 0) {
      topupFiredRef.current = false
    }
  }, [entries.length, wishlistEntries.length, totalEntries])

  useEffect(() => {
    if (
      canEditContent &&
      !isLoading &&
      totalEntries > 0 &&
      needsTopup &&
      !isGenerating &&
      !topupFiredRef.current
    ) {
      topupFiredRef.current = true
      topupMutation.mutate()
    }
  }, [canEditContent, totalEntries, needsTopup, isLoading, isGenerating, topupMutation])

  const handleDismiss = (appId: string, reason: DismissReason | null) => {
    const game = findEntryGame(appId)
    const title = game?.title ?? 'Recommendation'
    dismissMutation.mutate({ appId, reason })
    clearToastTimer()
    setToast({ appId, title, confirmedReason: reason })
    toastTimerRef.current = setTimeout(() => {
      setToast(null)
      toastTimerRef.current = null
    }, TOAST_DURATION_MS)
  }

  const handleUndoToast = () => {
    if (!toast) return
    restoreMutation.mutate(toast.appId)
    clearToastTimer()
    setToast(null)
  }

  const handlePickReasonFromToast = (reason: DismissReason) => {
    if (!toast) return
    setReasonMutation.mutate({ appId: toast.appId, reason })
    setToast({ ...toast, confirmedReason: reason })
    clearToastTimer()
    toastTimerRef.current = setTimeout(() => {
      setToast(null)
      toastTimerRef.current = null
    }, TOAST_CONFIRM_DURATION_MS)
  }

  const handleRestoreFromModal = (appId: string) => {
    restoreMutation.mutate(appId)
  }

  const handleSetReasonFromModal = (appId: string, reason: DismissReason) => {
    setReasonMutation.mutate({ appId, reason })
  }

  if (!activeGroupId) {
    return (
      <div className="cg-screen">
        <div className="cg-empty">Select or create a group to see recommendations.</div>
      </div>
    )
  }

  const generatedAt = data?.generatedAt ?? null
  const isRefreshing = refreshMutation.isPending || isGenerating || topupMutation.isPending

  let pending: RecommendationPending | undefined
  if (addToWishlistMutation.isPending && addToWishlistMutation.variables) {
    pending = { kind: 'addToWishlist', appId: addToWishlistMutation.variables }
  } else if (addToPlayingMutation.isPending && addToPlayingMutation.variables) {
    pending = { kind: 'addToPlaying', appId: addToPlayingMutation.variables }
  } else if (addToPlayedMutation.isPending && addToPlayedMutation.variables) {
    pending = { kind: 'addToPlayed', appId: addToPlayedMutation.variables }
  } else if (dismissMutation.isPending && dismissMutation.variables) {
    pending = { kind: 'dismiss', appId: dismissMutation.variables.appId }
  }

  const detailEntry =
    detailGameId !== null
      ? entries.find((e) => e.appId === detailGameId) ??
        wishlistEntries.find((e) => e.appId === detailGameId)
      : undefined

  const toolbarExtra =
    dismissedEntries.length > 0 ? (
      <button
        type="button"
        className="cg-dismiss-chip"
        onClick={() => setShowDismissedModal(true)}
        title="Restore games you've dismissed"
      >
        <Icon name="eye-off" size={12} />
        <span>Dismissed</span>
        <span className="cg-dismiss-chip-count">{dismissedEntries.length}</span>
      </button>
    ) : undefined

  const groupGuidance = activeGroup?.guidance ?? ''
  // Editors always see the tuning panel; voters see it read-only only when
  // there's existing guidance to read (no point showing an empty panel they
  // can't fill in).
  const tuningSlot = activeGroup && (canEditContent || groupGuidance) ? (
    <RecTuning
      groupName={activeGroup.name}
      groupGuidance={groupGuidance}
      isSavingGuidance={saveGuidanceMutation.isPending}
      onSaveGuidance={handleSaveGuidance}
      canEdit={canEditContent}
    />
  ) : undefined

  return (
    <>
      <RecommendationsView
        header={<GroupHeader sublabel="Recommendations" />}
        groupName={activeGroup?.name}
        groupSize={members.length}
        currentUserId={currentUser?.steamId}
        wishlistEntries={wishlistEntries}
        entries={entries}
        generatedAt={generatedAt}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        isGenerating={isGenerating}
        pending={pending}
        onRefresh={() => refreshMutation.mutate()}
        onAddToWishlist={(appId) => addToWishlistMutation.mutate(appId)}
        onAddToPlaying={(appId) => addToPlayingMutation.mutate(appId)}
        onAddToPlayed={(appId) => addToPlayedMutation.mutate(appId)}
        onDismiss={handleDismiss}
        onViewDetails={(appId) => setDetailGameId(appId)}
        toolbarExtra={toolbarExtra}
        tuningSlot={tuningSlot}
        canEdit={canEditContent}
      />

      {toast && (
        <UndoDismissToast
          title={toast.title}
          confirmedReason={toast.confirmedReason}
          onUndo={handleUndoToast}
          onPickReason={handlePickReasonFromToast}
        />
      )}

      {showDismissedModal && (
        <DismissedRecommendationsModal
          entries={dismissedEntries}
          groupName={activeGroup?.name ?? ''}
          onClose={() => setShowDismissedModal(false)}
          onRestore={handleRestoreFromModal}
          onSetReason={handleSetReasonFromModal}
          canEdit={canEditContent}
        />
      )}

      {detailEntry && (
        <GameDetailModal
          game={detailEntry.game}
          groupSize={members.length}
          onClose={() => setDetailGameId(null)}
        />
      )}

      {showRefreshPrompt &&
        createPortal(
          <div className="cg-modal-backdrop" onClick={() => setShowRefreshPrompt(false)}>
            <div
              className="cg-modal"
              style={{ width: 440 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cg-modal-header">
                <h2>Refresh recommendations?</h2>
              </div>
              <div
                style={{ padding: '14px 18px', color: 'var(--fg-1)', fontSize: 13, lineHeight: 1.5 }}
              >
                <p style={{ margin: 0 }}>
                  Your group preferences are saved. Recommendations refresh automatically
                  once a day — want to refresh now so your changes take effect right away?
                </p>
              </div>
              <div className="cg-modal-footer">
                <button
                  type="button"
                  className="cg-btn"
                  onClick={() => setShowRefreshPrompt(false)}
                >
                  Later
                </button>
                <button
                  type="button"
                  className="cg-btn primary"
                  onClick={() => {
                    setShowRefreshPrompt(false)
                    refreshMutation.mutate()
                  }}
                >
                  <Icon name="refresh" size={12} /> Refresh now
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
